import { formatAmount, formatCurrency } from 'components/Widget/shared/currency';
import { formatPercent } from 'components/Widget/shared/percent';
import type { CalculatorField, CalculatorFieldValues } from 'components/Widget/shared/types';
import type {
  ApySavingsBreakdownKey,
  ApySavingsBreakdownRow,
  ApySavingsSnapshot,
  ContributionFrequencyKey,
  ContributionFrequencyOption,
  SavingsPeriodKey,
  SavingsPeriodOption,
} from './types';

export const TITLE = 'APY Savings Calculator';

export const MONTHS_PER_YEAR = 12;

export const DEFAULT_APY = 1.0;

export const DEFAULT_STARTING_BALANCE = 200;

export const DEFAULT_RECURRING_CONTRIBUTION = 100;

export const MAX_FIELD_VALUE = 999999;

/** A recurring contribution of nothing makes the projection a flat interest curve, so the floor is $1. */
export const MIN_RECURRING_CONTRIBUTION = 1;

export const MAX_APY = 15;

export const SAVINGS_PERIOD_OPTIONS: readonly SavingsPeriodOption[] = [
  { key: 'threeMonths', label: '3 Months', bucketMonths: [1, 2, 3] },
  { key: 'sixMonths', label: '6 Months', bucketMonths: [1, 2, 3, 4, 5, 6] },
  { key: 'nineMonths', label: '9 Months', bucketMonths: [1, 3, 6, 9] },
  { key: 'oneYear', label: '1 Year', bucketMonths: [1, 3, 6, 9, 12] },
  { key: 'fiveYears', label: '5 Years', bucketMonths: [12, 24, 36, 48, 60] },
  { key: 'tenYears', label: '10 Years', bucketMonths: [24, 48, 72, 96, 120] },
  { key: 'twentyYears', label: '20 Years', bucketMonths: [12, 60, 120, 180, 240] },
];

export const DEFAULT_SAVINGS_PERIOD: SavingsPeriodOption['key'] = 'fiveYears';

export const CONTRIBUTION_FREQUENCY_OPTIONS: readonly ContributionFrequencyOption[] = [
  { key: 'weekly', label: 'Weekly', periodsPerYear: 52 },
  { key: 'biweekly', label: 'Biweekly', periodsPerYear: 26 },
  { key: 'monthly', label: 'Monthly', periodsPerYear: 12 },
  { key: 'quarterly', label: 'Quarterly', periodsPerYear: 4 },
  { key: 'annually', label: 'Annually', periodsPerYear: 1 },
];

export const DEFAULT_CONTRIBUTION_FREQUENCY: ContributionFrequencyOption['key'] = 'monthly';

// Both dropdowns are closed sets with defaults, so a key that misses is a coding error, not user input.
export const getSavingsPeriod = (key: SavingsPeriodKey): SavingsPeriodOption => {
  const option = SAVINGS_PERIOD_OPTIONS.find(period => period.key === key);

  if (!option) throw new Error(`Unsupported savings period: ${key}`);

  return option;
};

export const getContributionFrequency = (key: ContributionFrequencyKey): ContributionFrequencyOption => {
  const option = CONTRIBUTION_FREQUENCY_OPTIONS.find(frequency => frequency.key === key);

  if (!option) throw new Error(`Unsupported contribution frequency: ${key}`);

  return option;
};

/** Final copy from Legal, received 2026-09-11. */
export const DISCLAIMER =
  'This calculator is for educational purposes only. It makes estimates based on the information you provide, and your actual balance will differ as your deposits, your withdrawals, and the annual percentage yield in effect change over time. It is not an offer of any particular rate, a guarantee of future earnings, or tax, legal, financial, or investment advice.';

/** Greyed defaults rather than seeded values; `valueOrPlaceholder` calculates on them when untouched. */
export const STARTING_BALANCE_FIELD: CalculatorField = {
  label: 'Starting balance',
  hint: 'Enter the amount of money you’re starting with. If you do not have a starting balance yet, enter $0.',
  placeholder: formatAmount(DEFAULT_STARTING_BALANCE),
};

export const RECURRING_CONTRIBUTION_FIELD: CalculatorField = {
  label: 'Recurring contribution',
  hint: 'Enter the amount of money you plan to add to your savings on a weekly, biweekly, monthly, quarterly, or annual contribution basis.',
  placeholder: formatAmount(DEFAULT_RECURRING_CONTRIBUTION),
};

export const CONTRIBUTION_FREQUENCY_FIELD: CalculatorField = { label: 'How often do you make a contribution?' };

export const SAVINGS_PERIOD_FIELD: CalculatorField = {
  label: 'Savings time period',
  hint: 'Choose the number of months or years you plan to save.',
};

export const APY_FIELD: CalculatorField = {
  label: 'APY (annual percentage yield)',
  hint: 'Enter the annual percentage yield your savings account earns, which includes the effect of daily compound interest.',
  placeholder: formatPercent(DEFAULT_APY),
};

/** One range message per field, covering empty, zero and out-of-range; a red border alone fails WCAG 1.4.1. */
export const STARTING_BALANCE_ERROR = 'Enter a number between 0 and 999,999';

export const RECURRING_CONTRIBUTION_ERROR = 'Enter a number between 1 and 999,999';

export const APY_ERROR = 'Enter an APY between 0% and 15%';

/** Empty so the defaults render as grey placeholders; Calculate reads them back through `valueOrPlaceholder`. */
export const INITIAL_CURRENCY_VALUES: CalculatorFieldValues = {
  [STARTING_BALANCE_FIELD.label]: '',
  [RECURRING_CONTRIBUTION_FIELD.label]: '',
};

export const INITIAL_APY_VALUES: CalculatorFieldValues = { [APY_FIELD.label]: '' };

export const RESULT_COPY = {
  /** The dropdown label already carries the singular/plural, so the eyebrow cannot disagree with it. */
  eyebrow: (periodLabel: string) => `Your total est. savings after ${periodLabel.toLowerCase()}`,
  breakdownHeading: 'Total savings breakdown',
  ctaHeading: 'Fee-free banking with Chime.',
  ctaBody: 'Start banking with Chime today.',
  ctaLabel: 'Get started',
} as const;

/** Mirrors the mock's row order, which is not the bottom-to-top order the chart stacks in. */
export const BREAKDOWN_ROWS: readonly ApySavingsBreakdownRow[] = [
  { key: 'totalContributions', label: 'Total contribution' },
  { key: 'initialDeposit', label: 'Initial deposit' },
  { key: 'totalInterest', label: 'Total interest earned' },
];

/**
 * Read by both the bar segment and its breakdown swatch, so the two cannot drift. Adjacent fills
 * are deliberately under 3:1 because colour is not the sole means of distinguishing segments.
 */
export const SEGMENT_FILLS: Record<ApySavingsBreakdownKey, string> = {
  initialDeposit: 'bg-accent-green-600',
  totalContributions: 'bg-accent-green-400',
  totalInterest: 'bg-accent-green-200',
};

/** Bottom to top, so growth lightens upward. Not the order the breakdown rows print in. */
export const STACK_ORDER: readonly ApySavingsBreakdownKey[] = ['initialDeposit', 'totalContributions', 'totalInterest'];

/**
 * A period that never passes its first year stays in months throughout, so 1 Year ends on `12 mo`
 * rather than a lone `1 yr` among months. The longer periods keep `1 yr`, where it sits among years.
 */
const formatBucketLabel = (elapsedMonths: number, longestBucketMonths: number): string =>
  elapsedMonths % MONTHS_PER_YEAR === 0 && longestBucketMonths > MONTHS_PER_YEAR
    ? `${elapsedMonths / MONTHS_PER_YEAR} yr`
    : `${elapsedMonths} mo`;

export const CHART_COPY = {
  bucketLabel: formatBucketLabel,
  /** The bands print nothing, so each bar's name is the only place its bucket's growth is stated. */
  barLabel: (bucket: ApySavingsSnapshot, longestBucketMonths: number) =>
    `After ${formatBucketLabel(bucket.elapsedMonths, longestBucketMonths)}: ${formatCurrency(bucket.totalSavings)} total, made up of ${BREAKDOWN_ROWS.map(
      row => `${formatCurrency(bucket[row.key])} ${row.label.toLowerCase()}`,
    ).join(', ')}`,
} as const;

/**
 * Above this the hero abbreviates: a per-period contribution ceiling compounds to twelve digits,
 * which overflows the column. The exact figure stays in the hero's `sr-only` string either way.
 */
export const HERO_COMPACT_THRESHOLD = 10000000;

/** Self-contained copy for the live-region announcement; the panel itself is too long to hear out. */
export const SUMMARY_COPY = {
  total: (total: string, periodLabel: string) =>
    `Your total estimated savings after ${periodLabel.toLowerCase()} is ${total}.`,
} as const;
